import { useEffect, useState } from 'react';
import { Save, Inbox, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import type { ClientDocumentsConfig } from '@/hooks/useClientDocumentsConfig';

const withToken = (init?: RequestInit): RequestInit => {
  const token = localStorage.getItem('accessToken');
  return {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers || {}),
    },
  };
};

export function DocumentsSettingsSection({
  savingKey,
  setSavingKey,
}: {
  savingKey: string | null;
  setSavingKey: (k: string | null) => void;
}) {
  const [form, setForm] = useState<ClientDocumentsConfig>({
    documentsNasRoot: '\\\\NAS_GORI\\dati_nas\\Public\\Volume_1',
    appBaseUrl: 'https://vps-3dee2600.vps.ovh.net/',
    maintenanceMode: false,
    notificationDaysBefore: 0,
  });
  const [loading, setLoading] = useState(true);
  const [confirmMaint, setConfirmMaint] = useState(false);
  const [pendingMaint, setPendingMaint] = useState<boolean | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/settings/client-config', withToken());
        const json = await res.json();
        if (json.success && json.data) {
          setForm((prev) => ({ ...prev, ...json.data }));
        }
      } catch {
        toast.error('Impossibile caricare la configurazione documenti');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function persistDocuments(
    next: ClientDocumentsConfig,
    successMessage = 'Impostazioni documenti salvate'
  ) {
    setSavingKey('documents');
    try {
      const res = await fetch(
        '/api/settings/documents',
        withToken({
          method: 'PUT',
          body: JSON.stringify({
            documentsNasRoot: next.documentsNasRoot,
            appBaseUrl: next.appBaseUrl,
            maintenanceMode: next.maintenanceMode,
            notificationDaysBefore: next.notificationDaysBefore,
          }),
        })
      );
      const json = await res.json();
      if (json.success) {
        toast.success(successMessage);
        setForm((prev) => ({ ...prev, ...json.data }));
        window.dispatchEvent(new Event('settings:changed'));
      } else {
        toast.error(json.message || 'Errore nel salvataggio');
      }
    } catch {
      toast.error('Errore di rete');
    } finally {
      setSavingKey(null);
    }
  }

  async function save() {
    await persistDocuments(form);
  }

  const onMaintenanceToggle = (checked: boolean) => {
    if (checked) {
      setPendingMaint(true);
      setConfirmMaint(true);
      return;
    }
    setPendingMaint(null);
    void persistDocuments(
      { ...form, maintenanceMode: false },
      'Modalità manutenzione disattivata sul server'
    );
  };

  const confirmMaintenanceOn = () => {
    setConfirmMaint(false);
    setPendingMaint(null);
    void persistDocuments(
      { ...form, maintenanceMode: true },
      'Modalità manutenzione attiva sul server'
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-muted-foreground">Caricamento…</CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Inbox className="h-5 w-5" />
            Documenti e client desktop
          </CardTitle>
          <CardDescription>
            Percorso UNC del NAS (Volume_1), URL dell&apos;app per Electron, preavviso scadenze e manutenzione.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 max-w-2xl">
          <div className="space-y-2">
            <Label htmlFor="documentsNasRoot">Percorso documenti (NAS, UNC)</Label>
            <Input
              id="documentsNasRoot"
              value={form.documentsNasRoot}
              onChange={(e) => setForm((f) => ({ ...f, documentsNasRoot: e.target.value }))}
              className="font-mono text-sm"
              placeholder="\\NAS_GORI\dati_nas\Public\Volume_1"
            />
            <p className="text-xs text-muted-foreground">
              Deve terminare con la cartella autorizzata dal picker (es. <strong>Volume_1</strong>). Stesso casing consigliato ovunque.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="appBaseUrl">URL applicazione web (client MSI / Electron)</Label>
            <Input
              id="appBaseUrl"
              type="url"
              value={form.appBaseUrl}
              onChange={(e) => setForm((f) => ({ ...f, appBaseUrl: e.target.value }))}
              placeholder="https://vps-3dee2600.vps.ovh.net/"
            />
            <p className="text-xs text-muted-foreground">
              Origine consentita nella finestra desktop; modificabile solo da questa pagina (admin).
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notificationDaysBefore">Giorni prima della scadenza (notifiche)</Label>
            <Input
              id="notificationDaysBefore"
              type="number"
              min={0}
              max={365}
              value={form.notificationDaysBefore}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  notificationDaysBefore: Math.min(365, Math.max(0, parseInt(e.target.value, 10) || 0)),
                }))
              }
            />
            <p className="text-xs text-muted-foreground">
              0 = solo il giorno della scadenza (fuso Europe/Rome). Usato dal job notifiche scadenze.
            </p>
          </div>

          <div className="flex items-start justify-between gap-4 rounded-lg border border-amber-200 bg-amber-50/80 p-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 font-medium text-amber-900">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Modalità manutenzione
              </div>
              <p className="text-sm text-amber-900/90">
                Se attiva sul server, gli operatori non vedranno il gestionale finché un admin non disattiva l&apos;opzione (vedi messaggio di conferma).
              </p>
            </div>
            <Switch
              checked={form.maintenanceMode}
              onCheckedChange={onMaintenanceToggle}
              disabled={savingKey === 'documents'}
              className="shrink-0"
            />
          </div>

          <Button onClick={save} disabled={savingKey === 'documents'} className="bg-gray-800 text-white">
            <Save className="h-4 w-4 mr-2" />
            {savingKey === 'documents' ? 'Salvataggio…' : 'Salva'}
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={confirmMaint} onOpenChange={setConfirmMaint}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Attivare la modalità manutenzione?</AlertDialogTitle>
            <AlertDialogDescription>
              I non amministratori vedranno il sito in manutenzione e non potranno usare il gestionale fino alla disattivazione. Gli amministratori potranno accedere da login e disattivare la manutenzione dalle impostazioni.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setPendingMaint(null);
                setConfirmMaint(false);
              }}
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmMaintenanceOn}>Conferma</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
